/**
 * Date formatting bound to the interface language.
 *
 * Every date shown to the user (API values, local timestamps) goes through
 * these helpers so that switching language also switches month names, the
 * day/month order and the clock: 24-hour in French, AM/PM in English, ص/م in
 * Arabic. Never call `toLocaleString()` without a locale (it follows the
 * browser, not the application) nor hardcode 'fr-FR'.
 *
 * Examples below: French | English | Arabic.
 */

import i18n, { getBaseLanguage } from '../i18n';

import type { LanguageCode } from '../i18n';

/** Intl locale used for each interface language. */
const INTL_LOCALES: Record<LanguageCode, string> = {
  fr: 'fr-FR',
  en: 'en-US',
  // Latin digits, as everywhere else in the interface (references, amounts)
  ar: 'ar-u-nu-latn',
};

/** 15/09/2026 | 09/15/2026 | 15/09/2026 */
export const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

/** 15/09/2026 13:24 | 09/15/2026, 1:24 PM | 15/09/2026، 1:24 م */
export const DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  ...DATE_FORMAT,
  // 'numeric' so English reads 1:24 PM, not 01:24 PM (French keeps 09:05)
  hour: 'numeric',
  minute: '2-digit',
};

/** 15/09/2026 13:24:30 | 09/15/2026, 1:24:30 PM | 15/09/2026، 1:24:30 م */
export const DATE_TIME_SECONDS_FORMAT: Intl.DateTimeFormatOptions = {
  ...DATE_TIME_FORMAT,
  second: '2-digit',
};

/** 15 sept. 2026 | Sep 15, 2026 | 15 سبتمبر 2026 */
export const DATE_MEDIUM_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

/** 15 sept. 2026, 13:24 | Sep 15, 2026, 1:24 PM | 15 سبتمبر 2026، 1:24 م */
export const DATE_TIME_MEDIUM_FORMAT: Intl.DateTimeFormatOptions = {
  ...DATE_MEDIUM_FORMAT,
  hour: 'numeric',
  minute: '2-digit',
};

/** 15 septembre 2026 | September 15, 2026 | 15 سبتمبر 2026 */
export const DATE_LONG_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

export type DateInput = string | number | Date | null | undefined;

/** Intl locale of the current (or given) interface language. */
export function getDateLocale(language?: string): string {
  return INTL_LOCALES[getBaseLanguage(language || i18n.language)];
}

/** Whether the current (or given) interface language writes times with AM/PM. */
export function uses12HourClock(language?: string): boolean {
  const { hourCycle } = new Intl.DateTimeFormat(getDateLocale(language), {
    hour: 'numeric',
  }).resolvedOptions();
  return hourCycle === 'h11' || hourCycle === 'h12';
}

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Convert an API value to a Date, or null when empty or invalid.
 * A bare day ("2026-09-21") is read as local midnight: `new Date()` would read
 * it as UTC midnight and show the previous day west of Greenwich.
 */
export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' && DAY_ONLY.test(value)) {
    const [, year, month, day] = DAY_ONLY.exec(value)!;
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    date = new Date(value);
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

function format(
  value: DateInput,
  options: Intl.DateTimeFormatOptions,
  fallback: string,
  language?: string,
): string {
  const date = toDate(value);
  if (!date) return fallback;
  return date.toLocaleString(getDateLocale(language), options);
}

/** Format a date (no time) in the interface language. */
export function formatDate(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = DATE_FORMAT,
  fallback = '-',
  language?: string,
): string {
  return format(value, options, fallback, language);
}

/** Format a date and time in the interface language. */
export function formatDateTime(
  value: DateInput,
  options: Intl.DateTimeFormatOptions = DATE_TIME_FORMAT,
  fallback = '-',
  language?: string,
): string {
  return format(value, options, fallback, language);
}
